import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";

// Markdown renderer with workspace look-and-feel. Wraps react-markdown +
// remark-gfm. Used for request/folder/collection/workspace docs.

export default function Markdown({ source, className = "" }: { source: string; className?: string }) {
  return (
    <div className={"prose-rest " + className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        urlTransform={(url) => (url.startsWith("mention://") ? url : defaultUrlTransform(url))}
        components={{
          h1: ({ children }) => <h1 className="text-xl font-semibold tracking-tight mt-4 mb-2">{children}</h1>,
          h2: ({ children }) => <h2 className="text-lg font-semibold tracking-tight mt-4 mb-2">{children}</h2>,
          h3: ({ children }) => <h3 className="text-base font-semibold mt-3 mb-1.5">{children}</h3>,
          p:  ({ children }) => <p className="mb-2 leading-relaxed">{children}</p>,
          ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-1">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          a: ({ href, children }) => {
            // `mention://<slug>` links are produced by Comments.tsx when a
            // raw `@slug` matches a known workspace member. Render them as
            // accent chips with no outbound link.
            if (href && href.startsWith("mention://")) {
              return (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-accent-soft text-accent-bright font-medium text-[0.92em] no-underline">
                  {children}
                </span>
              );
            }
            return <a href={href} className="accent-link" target="_blank" rel="noreferrer">{children}</a>;
          },
          code: ({ children, className: cn }) => {
            const isBlock = (cn ?? "").includes("language-");
            if (isBlock) return <code className={cn}>{children}</code>;
            return <code className="px-1 py-0.5 rounded bg-surface-3 mono text-[0.9em]">{children}</code>;
          },
          pre: ({ children }) => <pre className="code-block overflow-x-auto whitespace-pre">{children}</pre>,
          blockquote: ({ children }) => <blockquote className="border-l-2 border-border pl-3 muted my-2">{children}</blockquote>,
          table: ({ children }) => <table className="border-collapse my-2 text-sm">{children}</table>,
          th: ({ children }) => <th className="border border-border px-2 py-1 text-left font-semibold">{children}</th>,
          td: ({ children }) => <td className="border border-border px-2 py-1">{children}</td>,
          hr: () => <hr className="my-4 border-border" />,
          strong: ({ children }) => <strong className="font-semibold text-text">{children}</strong>,
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}
